import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import type { cancelOrderItems } from "@redotech/redo-merchant-fulfillment-rpc/schema/cancel-order-items";
import { productIdFormat } from "@redotech/redo-product-id";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useState } from "react";
import type * as z from "zod";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export type ProductBinCounts = NonNullable<
  z.infer<typeof cancelOrderItems.input>["binCounts"]
>;

interface BinCountSelection {
  checked: boolean;
  quantity: number | undefined;
}

interface CancelItemBin {
  storageLocationId: ProductBinCounts["counts"][number]["storageLocationId"];
  binName: string;
  quantity: number;
  /** A count above 0 would need lots scanned, so these only go to 0. */
  lotTracked: boolean;
}

function selectionForBin(
  selections: Record<string, BinCountSelection>,
  bin: CancelItemBin,
  defaultBinName: string | null | undefined,
): BinCountSelection {
  return (
    selections[bin.storageLocationId] ?? {
      checked: !!defaultBinName && bin.binName === defaultBinName,
      quantity: 0,
    }
  );
}

/** Lets the user count a product's bins, the same way a WMS bin count does. */
export const CancelItemBinCounts = memo(function CancelItemBinCounts({
  redoProductId,
  crdbLocationId,
  defaultBinName,
  onChange,
}: {
  redoProductId: string | null | undefined;
  crdbLocationId: string | null | undefined;
  /** The bin the item was expected in, checked by default. */
  defaultBinName: string | null | undefined;
  onChange: (binCounts: ProductBinCounts | null) => void;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selections, setSelections] = useState<
    Record<string, BinCountSelection>
  >({});

  const locationId = locationIdFormat.schema.safeParse(crdbLocationId).data;
  const productId = productIdFormat.schema.safeParse(redoProductId).data;

  const binsLoad = useLoad(
    async (signal) => {
      if (!locationId || !productId) {
        return [];
      }
      const { bins } = await rpcClient.getBinsForProduct(
        {
          locationId,
          productId,
          includeStorage: true,
          includeCommitted: true,
          includeZeroAvailable: true,
          includeUncounted: true,
        },
        { signal },
      );
      // A bin count is per product, so lots in one bin collapse into one row.
      const byBin = new Map<string, CancelItemBin>();
      for (const bin of bins) {
        if (
          (bin.binType !== "bin" && bin.binType !== "storage") ||
          bin.totalQuantity <= 0
        ) {
          continue;
        }
        const existing = byBin.get(bin.storageLocationId);
        byBin.set(bin.storageLocationId, {
          storageLocationId: bin.storageLocationId,
          binName: bin.binName,
          quantity: (existing?.quantity ?? 0) + bin.totalQuantity,
          lotTracked: !!existing?.lotTracked || !!bin.inventoryItemId,
        });
      }
      return [...byBin.values()];
    },
    [rpcClient, locationId, productId],
  );
  const bins = binsLoad.value;

  const selectionFor = (bin: CancelItemBin) =>
    selectionForBin(selections, bin, defaultBinName);
  const updateSelection = (
    bin: CancelItemBin,
    update: Partial<BinCountSelection>,
  ) =>
    setSelections((current) => ({
      ...current,
      [bin.storageLocationId]: { ...selectionFor(bin), ...update },
    }));

  useEffect(() => {
    const counts = (bins ?? []).flatMap((bin) => {
      const selection = selectionForBin(selections, bin, defaultBinName);
      return selection.checked
        ? [
            {
              storageLocationId: bin.storageLocationId,
              quantity: bin.lotTracked ? 0 : (selection.quantity ?? 0),
            },
          ]
        : [];
    });
    onChange(productId && counts.length > 0 ? { productId, counts } : null);
  }, [bins, selections, defaultBinName, productId, onChange]);

  if (binsLoad.pending) {
    return (
      <Text fontSize="sm" textColor="secondary">
        {t("Loading bins...")}
      </Text>
    );
  }

  if (!bins?.length) {
    return (
      <Text fontSize="sm" textColor="secondary">
        {t("No bins have this item on hand.")}
      </Text>
    );
  }

  return (
    <Flex dir="column" gap="md">
      {bins.map((bin) => {
        const selection = selectionFor(bin);
        return (
          <Flex
            align="center"
            gap="md"
            justify="space-between"
            key={bin.storageLocationId}
          >
            <Flex dir="column" gap="xxs">
              <RedoCheckbox
                label={bin.binName}
                setValue={(value) =>
                  updateSelection(bin, { checked: value === true })
                }
                size="sm"
                value={selection.checked}
              />
              {bin.lotTracked && (
                <Text fontSize="xs" textColor="secondary">
                  {t("Lot-tracked bins can only be counted to 0 here")}
                </Text>
              )}
            </Flex>
            <Flex align="center" gap="sm">
              <Text fontSize="sm" textColor="secondary">
                {bin.quantity} →
              </Text>
              <RedoIncrementDecrement
                disabled={!selection.checked || bin.lotTracked}
                max={bin.quantity}
                min={0}
                setValue={(value) => updateSelection(bin, { quantity: value })}
                size="small"
                value={bin.lotTracked ? 0 : selection.quantity}
                widthHint="1000"
              />
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
});

/** Bin counts for one of several products, labelled with the product. */
export const ProductBinCountsSection = memo(function ProductBinCountsSection({
  productId,
  productTitle,
  crdbLocationId,
  defaultBinName,
  onChange,
}: {
  productId: string;
  productTitle: string;
  crdbLocationId: string | undefined;
  defaultBinName: string | null | undefined;
  onChange: (productId: string, binCounts: ProductBinCounts | null) => void;
}) {
  const handleChange = useCallback(
    (binCounts: ProductBinCounts | null) => onChange(productId, binCounts),
    [productId, onChange],
  );
  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="sm" fontWeight="medium">
        {productTitle}
      </Text>
      <CancelItemBinCounts
        crdbLocationId={crdbLocationId}
        defaultBinName={defaultBinName}
        onChange={handleChange}
        redoProductId={productId}
      />
    </Flex>
  );
});
